import { PrinterOutlined } from "@ant-design/icons";
import { Button, Modal } from "antd";
import dayjs from "dayjs";
import { useRef, useState, useEffect } from "react";
import Barcode from "react-barcode";
import { useAuth } from "../../contexts/AuthContext";
import { getEmpresa } from "../../services/empresaService";
import "./CierreDeCajaStyles.css";
import { downloadImage, convertHtmlToCanvas } from "../../utils/utils";
import { formatNumberByCountry } from "../../utils/formatNumbers";
import { diferenciaAceptable } from "../../services/margenDelArqueo";
import { toCents, fromCents, resolveMoneyDecimals } from "../../utils/money";
import { useConfig } from "../../contexts/ConfigContext";
import { identificadorFiscalDeLaEmpresa } from "../../utils/identificadorFiscalDeLaEmpresa";
import { nombreDelPaisEnPapel } from "../../utils/papelDelPais";

const CierreTicket = ({ open, onClose, cierre, detalles = [], caja, tpv }) => {
  const componentRef = useRef(null);
  const { currentUser } = useAuth();
  const [empresaData, setEmpresaData] = useState(null);
  const [tpvData, setTPVData] = useState(null);
  const [cajaData, setCajaData] = useState(null);
  const { country } = useConfig();

  // Formatear la fecha y hora actual
  const now = dayjs();
  const formattedDate = now.format("DD/MM/YYYY");
  const formattedTime = now.format("HH:mm:ss");

  // Cargar datos de la empresa
  useEffect(() => {
    const loadEmpresaData = async () => {
      try {
        const empresa = await getEmpresa();
        setEmpresaData(empresa);
      } catch (error) {
        console.error("Error al cargar datos de la empresa:", error);
        // Continuar sin datos de empresa
      }
    };

    if (open) {
      loadEmpresaData();
    }
  }, [open]);

  // Cargar datos del TPV
  useEffect(() => {
    const loadTPVData = async () => {
      try {
        // Si ya viene el TPV como string (número), cargarlo
        if (tpv) {
          const { getTPVByNoOrId } = await import("../../services/tpvService");
          const tpvInfo = await getTPVByNoOrId(tpv);
          setTPVData(tpvInfo);
        }
      } catch (error) {
        console.error("Error al cargar datos del TPV:", error);
        // Continuar sin datos de TPV
      }
    };

    if (open && tpv) {
      loadTPVData();
    }
  }, [open, tpv]);

  // Cargar datos de la Caja
  useEffect(() => {
    const loadCajaData = async () => {
      try {
        if (caja) {
          const { getCajaByNoOrId } = await import("../../services/cajaService");
          const cajaInfo = await getCajaByNoOrId(caja);
          setCajaData(cajaInfo);
        }
      } catch (error) {
        console.error("Error al cargar datos de la caja:", error);
        // Continuar sin datos de caja
      }
    };

    if (open && caja) {
      loadCajaData();
    }
  }, [open, caja]);

  // Calcular totales trabajando en unidades menores enteras para evitar redondeos intermedios
  const decimals = resolveMoneyDecimals(country);

  const calculateTotals = () => {
    const totalDetallesCents = detalles.reduce(
      (sum, detalle) => sum + toCents(detalle.MontoPago || 0, decimals),
      0
    );
    const importeCierreCents = toCents(cierre?.ImporteDeCierreUS || 0, decimals);
    const arqueoEfectivoCents = toCents(cierre?.ArqueoEfectivoUS || 0, decimals);
    const arqueoTarjetaCents = toCents(cierre?.ArqueoTarjetaUS || 0, decimals);
    const arqueoTotalCents = arqueoEfectivoCents + arqueoTarjetaCents;
    // ImporteDeCierreUS es solo ventas (sin apertura); el arqueo digitado de efectivo sí
    // incluye la apertura (conteo físico del cajón), así que hay que sumarla al esperado
    // para comparar ambos lados apertura-inclusive (mismo criterio que con CuadreMetodosPago).
    const importeAperturaCents = toCents(cierre?.ImporteDeApertura || 0, decimals);
    const diferenciaCents = arqueoTotalCents - (importeCierreCents + importeAperturaCents);

    return {
      totalDetalles: fromCents(totalDetallesCents, decimals),
      importeCierre: fromCents(importeCierreCents, decimals),
      arqueoEfectivo: fromCents(arqueoEfectivoCents, decimals),
      arqueoTarjeta: fromCents(arqueoTarjetaCents, decimals),
      arqueoTotal: fromCents(arqueoTotalCents, decimals),
      diferencia: fromCents(diferenciaCents, decimals),
    };
  };

  // Cuadre por forma de pago (cierre.CuadreMetodosPago), la misma información que se
  // envía a Business Central por ShiftsClosingLines: esperado (cobro), digitado y diferencia.
  const parseCuadreMetodosPago = () => {
    if (!cierre?.CuadreMetodosPago) return [];
    try {
      return JSON.parse(cierre.CuadreMetodosPago) || [];
    } catch (error) {
      console.error("Error al parsear CuadreMetodosPago:", error);
      return [];
    }
  };

  const cuadreMetodosPago = parseCuadreMetodosPago();
  const tieneCuadreDinamico = cuadreMetodosPago.length > 0;

  const totalesCuadre = cuadreMetodosPago.reduce(
    (acc, item) => {
      acc.cobroCents += toCents(item.cobro, decimals);
      acc.digitadoCents += toCents(item.digitado, decimals);
      return acc;
    },
    { cobroCents: 0, digitadoCents: 0 }
  );

  const {
    totalDetalles,
    importeCierre,
    arqueoEfectivo,
    arqueoTarjeta,
    diferencia: diferenciaBinaria,
  } = calculateTotals();

  // Arqueo total real: si hay cuadre por forma de pago (soporta N métodos), se usa esa suma
  // completa; si no (cierres antiguos sin este dato), se cae al esquema binario Efectivo/Tarjeta.
  const arqueoTotal = tieneCuadreDinamico
    ? fromCents(totalesCuadre.digitadoCents, decimals)
    : arqueoEfectivo + arqueoTarjeta;
  // Ojo: la diferencia se compara contra el total ESPERADO (totalesCuadre.cobroCents), no
  // contra importeCierre (ImporteDeCierreUS = solo ventas). El cobro esperado de Efectivo ya
  // incluye el importe de apertura, igual que lo digitado (conteo físico del cajón, que
  // también arrastra la apertura) — así ambos lados quedan apertura-inclusive. Comparar contra
  // importeCierre daba siempre una diferencia de ~ImporteDeApertura de más.
  const diferencia = tieneCuadreDinamico
    ? fromCents(totalesCuadre.digitadoCents - totalesCuadre.cobroCents, decimals)
    : diferenciaBinaria;

  // Función para imprimir directamente la imagen
  const printImage = async () => {
    try {
      const canvas = await convertHtmlToCanvas(componentRef.current);
      if (!canvas) return;

      // Obtener imagen como data URL
      const image = canvas.toDataURL("image/png");

      // Crear ventana para impresión
      const printWindow = window.open("", "_blank");

      if (!printWindow) {
        window.messageApi.warning(
          "El navegador bloqueó la ventana emergente. Por favor, permite ventanas emergentes para imprimir."
        );
        return;
      }

      // Escribir HTML con la imagen
      printWindow.document.write(`
                <html>
                <head>
                    <title>Comprobante de cierre ${cierre?.No || cierre?.id || ""}</title>
                    <style>
                        body {
                            margin: 0;
                            padding: 0;
                            display: flex;
                            justify-content: center;
                        }
                        img {
                            max-width: 100%;
                            height: auto;
                        }
                        @media print {
                            body {
                                width: 80mm; /* Ancho estándar para tickets */
                                margin: 0 auto;
                            }
                        }
                    </style>
                </head>
                <body>
                    <img src="${image}" alt="Ticket" />
                    <script>
                        // Imprimir automáticamente
                        window.onload = function() {
                            setTimeout(function() {
                                window.print();
                                setTimeout(function() { window.close(); }, 500);
                            }, 200);
                        };
                    </script>
                </body>
                </html>
            `);

      printWindow.document.close();

      // Notificar que se ha impreso y cerrar el modal automáticamente
      setTimeout(() => {
        window.messageApi.success("Comprobante enviado a impresión");
        onClose(); // Esto continuará con el proceso de cierre
      }, 1000);
    } catch (error) {
      console.error("Error al imprimir:", error);
      window.messageApi.error("Error al imprimir el ticket");
    }
  };

  // Función para descargar la imagen del ticket
  const downloadTicketImage = async () => {
    await downloadImage({
      element: componentRef.current,
      fileName: `cierre-${cierre?.No || cierre?.id || formattedDate.replace(/\//g, "-")}`,
      onSuccess: (msg) => window.messageApi.success(msg),
      onError: (msg) => window.messageApi.error(msg),
    });
  };

  return (
    <Modal
      title="Comprobante de Cierre de Caja - Paso Final"
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="download" onClick={downloadTicketImage}>
          Descargar Imagen
        </Button>,
        <Button key="close" onClick={onClose}>
          Continuar sin Imprimir
        </Button>,
        <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={printImage}>
          Imprimir y Continuar
        </Button>,
      ]}
      maskClosable={false}
      closable={false}
      width={400}
      centered
      className="ticket-preview-modal"
    >
      <div className="ticket-container">
        <div className="ticket-content" ref={componentRef}>
          {/* Cabecera del ticket */}
          <div className="ticket-header">
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
              <div
                style={{
                  fontWeight: "bold",
                  fontSize: "16px",
                  marginBottom: "8px",
                }}
              >
                {empresaData?.Name || "Empresa"}
              </div>
              <div style={{ fontSize: "14px", marginBottom: "8px" }}>
                {identificadorFiscalDeLaEmpresa(empresaData, country?.code)}
              </div>
              {tpvData?.nombre && (
                <div style={{ fontSize: "14px", marginBottom: "8px" }}>{tpvData.nombre}</div>
              )}
              {tpvData?.pais && (
                <div style={{ fontSize: "14px", marginBottom: "8px" }}>
                  {nombreDelPaisEnPapel(tpvData.pais)}
                </div>
              )}
              {tpvData?.nifCif && (
                <div style={{ fontSize: "14px", marginBottom: "8px" }}>{tpvData.nifCif}</div>
              )}
            </div>

            <div style={{ textAlign: "center", margin: "15px 0" }}>
              <div
                style={{
                  border: "1px solid #000",
                  display: "inline-block",
                  padding: "8px 16px",
                }}
              >
                <strong style={{ fontSize: "16px" }}>COMPROBANTE DE CIERRE</strong>
              </div>
            </div>

            <div style={{ margin: "20px 0" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "3px",
                  fontSize: "10px",
                }}
              >
                <span style={{ fontWeight: "bold" }}>CIERRE Nº:</span>
                <span>{cierre?.id ? String(cierre.id).padStart(6, "0") : "000001"}</span>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "3px",
                  fontSize: "10px",
                }}
              >
                <span style={{ fontWeight: "bold" }}>CAJA:</span>
                <span>{cajaData?.nombre || caja || cierre?.Caja || "1"}</span>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "3px",
                  fontSize: "10px",
                }}
              >
                <span style={{ fontWeight: "bold" }}>TPV:</span>
                <span>{tpvData?.nombre || tpv || cierre?.TPV || "1"}</span>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "3px",
                  fontSize: "10px",
                }}
              >
                <span style={{ fontWeight: "bold" }}>CAJERO:</span>
                <span>{cierre?.Cajero || currentUser?.No || "Usuario Sistema"}</span>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "3px",
                  fontSize: "10px",
                }}
              >
                <span style={{ fontWeight: "bold" }}>FECHA APERTURA:</span>
                <span>
                  {cierre?.FechaDeApertura
                    ? dayjs(cierre.FechaDeApertura).format("DD/MM/YYYY")
                    : formattedDate}
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "3px",
                  fontSize: "10px",
                }}
              >
                <span style={{ fontWeight: "bold" }}>FECHA CIERRE:</span>
                <span>
                  {cierre?.FechaDeCierre
                    ? dayjs(cierre.FechaDeCierre).format("DD/MM/YYYY")
                    : formattedDate}
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "3px",
                  fontSize: "10px",
                }}
              >
                <span style={{ fontWeight: "bold" }}>HORA IMPRESIÓN:</span>
                <span>{formattedTime}</span>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "3px",
                  fontSize: "10px",
                }}
              >
                <span style={{ fontWeight: "bold" }}>ESTADO:</span>
                {/* Sin color: era el único valor coloreado de todo el
                    comprobante, y pintaba «Cerrado» en rojo --que acá quiere
                    decir error-- cuando es el final normal de un turno. */}
                <span style={{ fontWeight: "bold" }}>{cierre?.Estado || "Cerrado"}</span>
              </div>
            </div>
          </div>

          <div style={{ borderTop: "1px dashed #000", margin: "15px 0" }}></div>

          {/* Información de montos */}
          <div style={{ marginBottom: "20px" }}>
            <h4
              style={{
                margin: "0 0 12px 0",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              RESUMEN DE CAJA
            </h4>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "6px",
                fontSize: "12px",
              }}
            >
              <span style={{ fontWeight: "bold" }}>IMPORTE APERTURA:</span>
              <span>{formatNumberByCountry(cierre?.ImporteDeApertura || 0, country, true)}</span>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "6px",
                fontSize: "12px",
              }}
            >
              <span style={{ fontWeight: "bold" }}>IMPORTE CIERRE:</span>
              <span>{formatNumberByCountry(importeCierre, country, true)}</span>
            </div>

            {tieneCuadreDinamico ? (
              cuadreMetodosPago.map((item) => (
                <div
                  key={item.codigoMetodoPago}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "6px",
                    fontSize: "12px",
                  }}
                >
                  <span style={{ fontWeight: "bold" }}>
                    ARQUEO {(item.nombre || item.codigoMetodoPago || "").toUpperCase()}:
                  </span>
                  <span>{formatNumberByCountry(item.digitado || 0, country, true)}</span>
                </div>
              ))
            ) : (
              <>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "6px",
                    fontSize: "12px",
                  }}
                >
                  <span style={{ fontWeight: "bold" }}>ARQUEO EFECTIVO:</span>
                  <span>{formatNumberByCountry(arqueoEfectivo, country, true)}</span>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "6px",
                    fontSize: "12px",
                  }}
                >
                  <span style={{ fontWeight: "bold" }}>ARQUEO TARJETA:</span>
                  <span>{formatNumberByCountry(arqueoTarjeta, country, true)}</span>
                </div>
              </>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "6px",
                fontSize: "12px",
                borderTop: "1px solid #ccc",
                paddingTop: "4px",
                marginTop: "4px",
              }}
            >
              <span style={{ fontWeight: "bold" }}>ARQUEO TOTAL:</span>
              <span>{formatNumberByCountry(arqueoTotal, country, true)}</span>
            </div>

            <div
              style={{
                borderTop: "1px solid #000",
                paddingTop: "8px",
                marginTop: "8px",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>DIFERENCIA:</span>
                <span
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    color: diferenciaAceptable(diferencia, tpvData) ? "#52c41a" : "#f50",
                  }}
                >
                  {formatNumberByCountry(diferencia, country, true)}
                </span>
              </div>
            </div>
          </div>

          {/* Cuadre por forma de pago: mismo detalle enviado a Business Central (ShiftsClosingLines) */}
          {(tieneCuadreDinamico || (detalles && detalles.length > 0)) && (
            <>
              <div style={{ borderTop: "1px dashed #000", margin: "15px 0" }}></div>
              <div style={{ marginBottom: "20px" }}>
                <h4
                  style={{
                    margin: "0 0 12px 0",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  CUADRE POR FORMA DE PAGO
                </h4>

                {tieneCuadreDinamico ? (
                  <>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1.6fr 1fr 1fr 1fr",
                        gap: "6px",
                        fontWeight: "bold",
                        marginBottom: "8px",
                        borderBottom: "1px solid #000",
                        paddingBottom: "4px",
                        fontSize: "11px",
                      }}
                    >
                      <span>FORMA DE PAGO</span>
                      <span style={{ textAlign: "right" }}>ESPERADO</span>
                      <span style={{ textAlign: "right" }}>DIGITADO</span>
                      <span style={{ textAlign: "right" }}>DIF.</span>
                    </div>

                    {cuadreMetodosPago.map((item) => (
                      <div
                        key={item.codigoMetodoPago}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1.6fr 1fr 1fr 1fr",
                          gap: "6px",
                          marginBottom: "6px",
                          fontSize: "10px",
                        }}
                      >
                        <span>{(item.nombre || item.codigoMetodoPago || "").toUpperCase()}</span>
                        <span style={{ textAlign: "right" }}>
                          {formatNumberByCountry(item.cobro || 0, country, true)}
                        </span>
                        <span style={{ textAlign: "right", fontWeight: "bold" }}>
                          {formatNumberByCountry(item.digitado || 0, country, true)}
                        </span>
                        <span
                          style={{
                            textAlign: "right",
                            color: diferenciaAceptable(item.diferencia, tpvData) ? "#52c41a" : "#f50",
                          }}
                        >
                          {formatNumberByCountry(item.diferencia || 0, country, true)}
                        </span>
                      </div>
                    ))}

                    <div
                      style={{
                        borderTop: "1px solid #000",
                        paddingTop: "8px",
                        marginTop: "8px",
                      }}
                    >
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1.6fr 1fr 1fr 1fr",
                          gap: "6px",
                        }}
                      >
                        <span style={{ fontWeight: "bold", fontSize: "12px" }}>TOTAL:</span>
                        <span
                          style={{
                            fontWeight: "bold",
                            fontSize: "12px",
                            textAlign: "right",
                          }}
                        >
                          {formatNumberByCountry(
                            fromCents(totalesCuadre.cobroCents, decimals),
                            country,
                            true
                          )}
                        </span>
                        <span
                          style={{
                            fontWeight: "bold",
                            fontSize: "12px",
                            textAlign: "right",
                          }}
                        >
                          {formatNumberByCountry(
                            fromCents(totalesCuadre.digitadoCents, decimals),
                            country,
                            true
                          )}
                        </span>
                        <span
                          style={{
                            fontWeight: "bold",
                            fontSize: "12px",
                            textAlign: "right",
                          }}
                        >
                          {formatNumberByCountry(
                            fromCents(totalesCuadre.digitadoCents - totalesCuadre.cobroCents, decimals),
                            country,
                            true
                          )}
                        </span>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Cierres antiguos sin cuadre por forma de pago guardado */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "2fr 1fr",
                        gap: "8px",
                        fontWeight: "bold",
                        marginBottom: "8px",
                        borderBottom: "1px solid #000",
                        paddingBottom: "4px",
                        fontSize: "12px",
                      }}
                    >
                      <span>FORMA DE PAGO</span>
                      <span style={{ textAlign: "right" }}>MONTO</span>
                    </div>

                    {detalles.map((detalle, index) => (
                      <div
                        key={index}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "2fr 1fr",
                          gap: "8px",
                          marginBottom: "6px",
                          fontSize: "11px",
                        }}
                      >
                        <span>{detalle.DesFormaPago || detalle.idFormaPago}</span>
                        <span style={{ textAlign: "right", fontWeight: "bold" }}>
                          {formatNumberByCountry(detalle?.MontoPago || 0, country, true)}
                        </span>
                      </div>
                    ))}

                    <div
                      style={{
                        borderTop: "1px solid #000",
                        paddingTop: "8px",
                        marginTop: "8px",
                      }}
                    >
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "2fr 1fr",
                          gap: "8px",
                        }}
                      >
                        <span style={{ fontWeight: "bold", fontSize: "12px" }}>TOTAL:</span>
                        <span
                          style={{
                            fontWeight: "bold",
                            fontSize: "12px",
                            textAlign: "right",
                          }}
                        >
                          {formatNumberByCountry(totalDetalles, country, true)}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </>
          )}

          {/* Información adicional */}
          {cierre?.Comprobante_Tarjetas && (
            <>
              <div style={{ borderTop: "1px dashed #000", margin: "15px 0" }}></div>
              <div style={{ marginBottom: "20px" }}>
                <h4
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  INFORMACIÓN ADICIONAL
                </h4>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "4px",
                    fontSize: "12px",
                  }}
                >
                  <span>Total Tarjetas:</span>
                  <span style={{ fontWeight: "bold" }}>{cierre.Comprobante_Tarjetas}</span>
                </div>
              </div>
            </>
          )}

          <div style={{ borderTop: "1px dashed #000", margin: "20px 0" }}></div>

          <div style={{ textAlign: "center", marginBottom: "20px" }}>
            <div style={{ fontSize: "16px", fontWeight: "bold" }}>CIERRE DE CAJA COMPLETADO</div>
          </div>

          <div
            style={{
              textAlign: "center",
              marginBottom: "15px",
              padding: "8px",
              backgroundColor: "#fff2e8",
              border: "1px solid #ffa940",
              borderRadius: "4px",
            }}
          >
            <div style={{ fontSize: "12px", fontWeight: "bold", color: "#ad6800" }}>
              DOCUMENTO INTERNO DE CONTROL
            </div>
          </div>

          <div style={{ textAlign: "center", marginTop: "20px" }}>
            <Barcode
              value={cierre?.id ? String(cierre.id).padStart(6, "0") : "000001"}
              width={1.5}
              height={40}
              fontSize={12}
              marginTop={10}
              displayValue={true}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CierreTicket;
